import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { getProductGroups } from "../../common/api/authApi";
import {
  getProductGroupDisplayName,
} from "../../common/libs/productGroupLocale";
import { normalizedLocale } from "../../common/libs/normalizedLocale";
import { product_groups } from "../reference/product_groups";

type RefProductGroup = {
  id: number;
  code?: string;
  name?: string;
  nameRu?: string;
  nameKk?: string;
};

// Товарные группы с названием на языке интерфейса — через общий getProductGroupDisplayName
// (как в профиле и регистрации). Справочник грузится один раз, название пересчитывается
// при смене языка. Пока справочник не загрузился — локальный product_groups.
export function useProductGroupOptions() {
  const { i18n } = useTranslation();
  const locale = normalizedLocale(i18n.language);
  const [groups, setGroups] = useState<RefProductGroup[]>(() =>
    product_groups.map((group: any) => ({ id: group.id, code: group.code, name: group.name })),
  );

  useEffect(() => {
    getProductGroups()
      .then((response: any) => {
        const list = Array.isArray(response) ? response : response?.data ?? [];
        const loaded = list
          .filter((group: any) => group?.id != null)
          .map((group: any) => ({
            id: Number(group.id),
            code: group.code ?? group.productGroupCode,
            name: group.name ?? group.productGroupName,
            nameRu: group.nameRu,
            nameKk: group.nameKk,
          }));
        if (loaded.length) setGroups(loaded);
      })
      .catch(() => undefined);
  }, []);

  return useMemo(() => {
    const labelOf = (group: RefProductGroup) =>
      getProductGroupDisplayName(group, locale) || String(group.code ?? group.id);

    const options = groups.map((group) => ({ value: String(group.id), label: labelOf(group) }));
    const optionsByCode = groups
      .filter((group) => group.code)
      .map((group) => ({ value: String(group.code), label: labelOf(group) }));

    const nameById = (id?: number | null) => {
      const group = groups.find((g) => g.id === Number(id));
      return group ? labelOf(group) : id != null ? String(id) : "-";
    };
    // Для данных бэка с кодом ТГ: название по коду, иначе то, что пришло
    const nameByCode = (code?: string | null, fallback?: string | null) => {
      if (!code) return fallback || "-";
      const group = groups.find((g) => g.code === code);
      return (
        (group ? labelOf(group) : getProductGroupDisplayName({ code, name: fallback ?? "" }, locale)) ||
        fallback ||
        code
      );
    };

    return { options, optionsByCode, nameById, nameByCode };
  }, [groups, locale]);
}
